import {
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { YALO_RADIUS, YALO_SEMANTIC } from '@commerce/tokens'

const TITLE_ID = 'visit-sheet-title'

interface Props {
  open: boolean
  days: readonly string[]
  /** The day already chosen, so reopening the sheet does not lose it. */
  selectedDay: string | null
  onClose: () => void
  onConfirm: (day: string) => void
}

/**
 * The "día de visita" picker: pick a weekday, then confirm it.
 *
 * A bottom `Drawer` because that is the design's modal on a phone, and the
 * theme already rounds `paperAnchorBottom`. Everything the brief asks of an
 * overlay comes from `Modal` underneath it — focus is trapped while it is open,
 * Escape and the backdrop close it, and focus returns to whatever opened it —
 * so none of that is reimplemented here. What *is* added is the dialog
 * semantics: `Drawer` gives its paper no role, so a screen reader would
 * otherwise announce an unnamed region rather than a modal dialog.
 *
 * The days are a `ToggleButtonGroup` rather than radios: the design draws pills,
 * and a toggle group is a real group of real buttons that announce their
 * pressed state. Confirming is a separate action, so picking a day is a draft
 * until the shopper says so — which is why the pending choice is local state
 * and only `onConfirm` reports it.
 */
export const VisitSheet = ({ open, days, selectedDay, onClose, onConfirm }: Props) => {
  const [pending, setPending] = useState<string | null>(selectedDay)

  // Re-seed on each opening rather than on every render: while the sheet is
  // open the draft is the shopper's, not the parent's.
  useEffect(() => {
    if (open) setPending(selectedDay)
  }, [open, selectedDay])

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        role: 'dialog',
        'aria-modal': true,
        'aria-labelledby': TITLE_ID,
      }}
    >
      <Box sx={{ p: 2.5, pb: 'calc(20px + env(safe-area-inset-bottom))' }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Typography id={TITLE_ID} variant="h3" component="h2">
            Día de visita
          </Typography>
          <IconButton onClick={onClose} aria-label="Cerrar" sx={{ mt: -0.5, mr: -1 }}>
            ×
          </IconButton>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Tu asesora visita tu tienda cada semana. Elige el día que te acomoda.
        </Typography>

        <ToggleButtonGroup
          exclusive
          value={pending}
          onChange={(_event, day: string | null) => {
            // `null` arrives when the pressed day is pressed again. Unselecting
            // would leave nothing to confirm, so the current day stands.
            if (day !== null) setPending(day)
          }}
          aria-label="Día de visita"
          sx={{
            mt: 2,
            flexWrap: 'wrap',
            gap: 0.75,
            '& .MuiToggleButtonGroup-grouped': {
              border: `1px solid ${YALO_SEMANTIC.borderDefault}`,
              borderRadius: `${YALO_RADIUS.pill}px`,
              height: 34,
              px: 1.75,
              fontSize: 13,
              fontWeight: 600,
              color: YALO_SEMANTIC.fg2,
              textTransform: 'none',
            },
            '& .MuiToggleButtonGroup-grouped.Mui-selected': {
              bgcolor: 'primary.main',
              borderColor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' },
            },
          }}
        >
          {days.map((day) => (
            <ToggleButton key={day} value={day}>
              {day}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Button
          fullWidth
          size="large"
          variant="contained"
          disabled={pending === null}
          onClick={() => {
            if (pending !== null) onConfirm(pending)
          }}
          sx={{ mt: 2.5 }}
        >
          Confirmar día
        </Button>
      </Box>
    </Drawer>
  )
}
